import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArchiveIcon from '@mui/icons-material/Archive';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import QuizIcon from '@mui/icons-material/Quiz';
import RestoreIcon from '@mui/icons-material/Restore';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import OrganizationEmptyState from '../components/OrganizationEmptyState';

const TAB_KEYS = ['workflows', 'skus'];

function getTabIndexFromHash(hash) {
  const value = (hash || '').replace('#', '').toLowerCase();
  const index = TAB_KEYS.indexOf(value);
  return index >= 0 ? index : 0;
}

export default function LabWorkflows() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const location = useLocation();
  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(() => getTabIndexFromHash(window.location.hash));

  const [workflows, setWorkflows] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [workflowsTotal, setWorkflowsTotal] = useState(0);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsError, setWorkflowsError] = useState(null);
  const [workflowsPage, setWorkflowsPage] = useState(0);
  const [workflowsRowsPerPage, setWorkflowsRowsPerPage] = useState(10);
  const [workflowRefreshNonce, setWorkflowRefreshNonce] = useState(0);
  const [workflowSearchInput, setWorkflowSearchInput] = useState('');
  const [workflowSearchText, setWorkflowSearchText] = useState('');
  const [workflowArchivedFilter, setWorkflowArchivedFilter] = useState('active');
  const [workflowOrderBy, setWorkflowOrderBy] = useState('created_at');
  const [workflowOrderDir, setWorkflowOrderDir] = useState('desc');

  const [skuRows, setSkuRows] = useState([]);
  const [skuTotal, setSkuTotal] = useState(0);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuError, setSkuError] = useState(null);
  const [skuPage, setSkuPage] = useState(0);
  const [skuRowsPerPage, setSkuRowsPerPage] = useState(10);
  const [skuRefreshNonce, setSkuRefreshNonce] = useState(0);
  const [skuSearchInput, setSkuSearchInput] = useState('');
  const [skuSearchText, setSkuSearchText] = useState('');
  const [skuWorkflowFilter, setSkuWorkflowFilter] = useState('__all__');
  const [skuArchivedFilter, setSkuArchivedFilter] = useState('active');
  const [skuOrderBy, setSkuOrderBy] = useState('created_at');
  const [skuOrderDir, setSkuOrderDir] = useState('desc');

  const [createWorkflowDialogOpen, setCreateWorkflowDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [createWorkflowError, setCreateWorkflowError] = useState(null);
  const [createWorkflowLoading, setCreateWorkflowLoading] = useState(false);
  const [editQuestionsDialogOpen, setEditQuestionsDialogOpen] = useState(false);
  const [questionsWorkflow, setQuestionsWorkflow] = useState(null);
  const [workflowQuestions, setWorkflowQuestions] = useState([]);
  const [workflowQuestionsFilter, setWorkflowQuestionsFilter] = useState('active');
  const [workflowQuestionsError, setWorkflowQuestionsError] = useState(null);
  const [workflowQuestionsLoading, setWorkflowQuestionsLoading] = useState(false);
  const [workflowQuestionsSaving, setWorkflowQuestionsSaving] = useState(false);

  const [skuDialogOpen, setSkuDialogOpen] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [skuDialogName, setSkuDialogName] = useState('');
  const [skuDialogNumber, setSkuDialogNumber] = useState('');
  const [skuDialogWorkflowId, setSkuDialogWorkflowId] = useState('');
  const [skuDialogError, setSkuDialogError] = useState(null);
  const [skuDialogLoading, setSkuDialogLoading] = useState(false);

  useEffect(() => {
    setTabIndex(getTabIndexFromHash(location.hash));
  }, [location.hash]);

  useEffect(() => {
    setWorkflowsPage(0);
    setSkuPage(0);
  }, [selectedPractice?.id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setWorkflowSearchText(workflowSearchInput);
      setWorkflowsPage(0);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [workflowSearchInput]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSkuSearchText(skuSearchInput);
      setSkuPage(0);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [skuSearchInput]);

  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) {
      setWorkflows([]);
      setWorkflowsTotal(0);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      archived_filter: workflowArchivedFilter,
      order_by: workflowOrderBy,
      order_dir: workflowOrderDir,
      skip: String(workflowsPage * workflowsRowsPerPage),
      limit: String(workflowsRowsPerPage),
    });
    if (workflowSearchText) {
      params.append('search_text', workflowSearchText);
    }

    setWorkflowsLoading(true);
    setWorkflowsError(null);

    fetch(`${apiBase}/lab/lab-test-workflows?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch lab workflows');
        }
        return res.json();
      })
      .then((data) => {
        setWorkflows(data.items || []);
        setWorkflowsTotal(data.total || 0);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setWorkflowsError(err.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setWorkflowsLoading(false);
        }
      });

    return () => controller.abort();
  }, [
    apiBase,
    workflowArchivedFilter,
    workflowOrderBy,
    workflowOrderDir,
    selectedPractice?.id,
    tokens?.id_token,
    workflowRefreshNonce,
    workflowsPage,
    workflowSearchText,
    workflowsRowsPerPage,
  ]);

  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) {
      setWorkflowOptions([]);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      archived_filter: 'active',
      skip: '0',
      limit: '1000',
    });

    fetch(`${apiBase}/lab/lab-test-workflows?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch workflow options');
        }
        return res.json();
      })
      .then((data) => {
        setWorkflowOptions(data.items || []);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setWorkflowOptions([]);
        }
      });

    return () => controller.abort();
  }, [apiBase, selectedPractice?.id, tokens?.id_token, workflowRefreshNonce]);

  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) {
      setSkuRows([]);
      setSkuTotal(0);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      organization_id: selectedPractice.id,
      archived_filter: skuArchivedFilter,
      order_by: skuOrderBy,
      order_dir: skuOrderDir,
      skip: String(skuPage * skuRowsPerPage),
      limit: String(skuRowsPerPage),
    });
    if (skuSearchText) {
      params.append('search_text', skuSearchText);
    }
    if (skuWorkflowFilter) {
      params.append('workflow_filter', skuWorkflowFilter);
    }

    setSkuLoading(true);
    setSkuError(null);

    fetch(`${apiBase}/lab/lab-test-skus?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch lab test SKUs');
        }
        return res.json();
      })
      .then((data) => {
        setSkuRows(data.items || []);
        setSkuTotal(data.total || 0);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setSkuError(err.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSkuLoading(false);
        }
      });

    return () => controller.abort();
  }, [
    apiBase,
    skuArchivedFilter,
    skuOrderBy,
    skuOrderDir,
    selectedPractice?.id,
    skuPage,
    skuRefreshNonce,
    skuRowsPerPage,
    skuSearchText,
    skuWorkflowFilter,
    tokens?.id_token,
  ]);

  const handleTabChange = (_, newValue) => {
    setTabIndex(newValue);
    navigate(`${location.pathname}#${TAB_KEYS[newValue]}`, { replace: true });
  };

  const handleWorkflowSort = (field) => {
    if (workflowOrderBy === field) {
      setWorkflowOrderDir((value) => (value === 'asc' ? 'desc' : 'asc'));
    } else {
      setWorkflowOrderBy(field);
      setWorkflowOrderDir('asc');
    }
    setWorkflowsPage(0);
  };

  const handleSkuSort = (field) => {
    if (skuOrderBy === field) {
      setSkuOrderDir((value) => (value === 'asc' ? 'desc' : 'asc'));
    } else {
      setSkuOrderBy(field);
      setSkuOrderDir('asc');
    }
    setSkuPage(0);
  };

  const normalizeQuestionDraft = (question, index) => ({
    id: question.id || null,
    draftId: question.id || `new-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    question_text: question.question_text || '',
    is_active: question.is_active !== false,
    answer_type: question.answer_type && question.answer_type !== 'string' ? question.answer_type : 'YesNo',
    order_index: typeof question.order_index === 'number' ? question.order_index : index,
  });

  const handleOpenEditQuestionsDialog = async (workflow) => {
    if (!workflow?.id || !tokens?.id_token) {
      return;
    }
    setQuestionsWorkflow(workflow);
    setWorkflowQuestions([]);
    setWorkflowQuestionsFilter('active');
    setWorkflowQuestionsError(null);
    setWorkflowQuestionsLoading(true);
    setEditQuestionsDialogOpen(true);

    try {
      const response = await fetch(`${apiBase}/lab/lab-test-workflows/${workflow.id}`, {
        headers: {
          Authorization: `Bearer ${tokens.id_token}`,
        },
      });
      if (!response.ok) {
        let message = 'Failed to load workflow questions';
        try {
          const data = await response.json();
          if (data?.detail) {
            message = data.detail;
          }
        } catch {
          // Keep fallback.
        }
        throw new Error(message);
      }
      const data = await response.json();
      const nextQuestions = (data.questions || [])
        .slice()
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map((question, index) => normalizeQuestionDraft(question, index));
      setWorkflowQuestions(nextQuestions);
      setQuestionsWorkflow(data);
    } catch (err) {
      setWorkflowQuestionsError(err.message || 'Failed to load workflow questions');
    } finally {
      setWorkflowQuestionsLoading(false);
    }
  };

  const handleCloseEditQuestionsDialog = () => {
    if (workflowQuestionsSaving) {
      return;
    }
    setEditQuestionsDialogOpen(false);
    setQuestionsWorkflow(null);
    setWorkflowQuestions([]);
    setWorkflowQuestionsFilter('active');
    setWorkflowQuestionsError(null);
    setWorkflowQuestionsLoading(false);
    setWorkflowQuestionsSaving(false);
  };

  const handleWorkflowQuestionChange = (draftId, changes) => {
    setWorkflowQuestions((current) =>
      current.map((question) => (question.draftId === draftId ? { ...question, ...changes } : question))
    );
  };

  const handleAddWorkflowQuestionDraft = () => {
    setWorkflowQuestions((current) => [
      ...current,
      normalizeQuestionDraft(
        {
          question_text: '',
          is_active: true,
          answer_type: 'YesNo',
          order_index: current.length,
        },
        current.length
      ),
    ]);
  };

  const handleMoveWorkflowQuestionDraft = (draftId, direction) => {
    setWorkflowQuestions((current) => {
      const index = current.findIndex((question) => question.draftId === draftId);
      if (index < 0) {
        return current;
      }
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }
      const next = current.slice();
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next.map((question, orderIndex) => ({ ...question, order_index: orderIndex }));
    });
  };

  const handleSaveWorkflowQuestions = async () => {
    if (!questionsWorkflow?.id || !tokens?.id_token) {
      return;
    }
    const normalizedQuestions = workflowQuestions.map((question, index) => ({
      ...question,
      question_text: question.question_text.trim(),
      answer_type: question.answer_type || 'YesNo',
      order_index: index,
    }));
    const emptyQuestion = normalizedQuestions.find((question) => !question.question_text);
    if (emptyQuestion) {
      setWorkflowQuestionsError('Every question must have text before saving.');
      return;
    }

    setWorkflowQuestionsSaving(true);
    setWorkflowQuestionsError(null);

    try {
      const response = await fetch(`${apiBase}/lab/lab-test-workflows/${questionsWorkflow.id}/questions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`,
        },
        body: JSON.stringify({
          questions: normalizedQuestions.map((question, index) => ({
            id: question.id || undefined,
            question_text: question.question_text,
            is_active: question.is_active,
            answer_type: question.answer_type || 'YesNo',
            order_index: index,
          })),
        }),
      });

      if (!response.ok) {
        let message = 'Failed to save workflow questions';
        try {
          const data = await response.json();
          if (data?.detail) {
            message = data.detail;
          }
        } catch {
          // Keep fallback.
        }
        throw new Error(message);
      }

      handleCloseEditQuestionsDialog();
      setWorkflowRefreshNonce((value) => value + 1);
    } catch (err) {
      setWorkflowQuestionsError(err.message || 'Failed to save workflow questions');
    } finally {
      setWorkflowQuestionsSaving(false);
    }
  };

  const handleOpenCreateWorkflowDialog = () => {
    setEditingWorkflow(null);
    setCreateWorkflowError(null);
    setNewWorkflowName('');
    setCreateWorkflowDialogOpen(true);
  };

  const handleOpenEditWorkflowDialog = (workflow) => {
    setEditingWorkflow(workflow);
    setCreateWorkflowError(null);
    setNewWorkflowName(workflow.name || '');
    setCreateWorkflowDialogOpen(true);
  };

  const handleCloseCreateWorkflowDialog = () => {
    if (createWorkflowLoading) {
      return;
    }
    setCreateWorkflowDialogOpen(false);
    setEditingWorkflow(null);
    setCreateWorkflowError(null);
    setNewWorkflowName('');
  };

  const handleSaveWorkflow = async () => {
    const trimmedName = newWorkflowName.trim();
    if (!trimmedName || !selectedPractice?.id || !tokens?.id_token) {
      setCreateWorkflowError('Workflow name is required.');
      return;
    }

    setCreateWorkflowLoading(true);
    setCreateWorkflowError(null);

    try {
      const isEditing = Boolean(editingWorkflow);
      const response = await fetch(
        isEditing
          ? `${apiBase}/lab/lab-test-workflows/${editingWorkflow.id}`
          : `${apiBase}/lab/lab-test-workflows`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.id_token}`,
          },
          body: JSON.stringify(
            isEditing
              ? { name: trimmedName }
              : {
                  organization_id: selectedPractice.id,
                  name: trimmedName,
                }
          ),
        }
      );

      if (!response.ok) {
        let message = isEditing ? 'Failed to update workflow' : 'Failed to create workflow';
        try {
          const data = await response.json();
          if (data?.detail) {
            message = data.detail;
          }
        } catch {
          // Keep fallback.
        }
        throw new Error(message);
      }

      setCreateWorkflowDialogOpen(false);
      setEditingWorkflow(null);
      setNewWorkflowName('');
      setWorkflowsPage(0);
      setWorkflowRefreshNonce((value) => value + 1);
    } catch (err) {
      setCreateWorkflowError(err.message || 'Failed to save workflow');
    } finally {
      setCreateWorkflowLoading(false);
    }
  };

  const handleArchiveWorkflow = async (workflow) => {
    if (!tokens?.id_token) {
      return;
    }
    if (!window.confirm(`Archive workflow ${workflow.name}?`)) {
      return;
    }
    try {
      const response = await fetch(`${apiBase}/lab/lab-test-workflows/${workflow.id}/archive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.id_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to archive workflow');
      }
      setWorkflowRefreshNonce((value) => value + 1);
    } catch (err) {
      setWorkflowsError(err.message || 'Failed to archive workflow');
    }
  };

  const handleOpenCreateSkuDialog = () => {
    setEditingSku(null);
    setSkuDialogError(null);
    setSkuDialogName('');
    setSkuDialogNumber('');
    setSkuDialogWorkflowId('');
    setSkuDialogOpen(true);
  };

  const handleOpenEditSkuDialog = (sku) => {
    setEditingSku(sku);
    setSkuDialogError(null);
    setSkuDialogName(sku.name || '');
    setSkuDialogNumber(sku.sku_number || '');
    setSkuDialogWorkflowId(sku.lab_test_workflow_id || '');
    setSkuDialogOpen(true);
  };

  const handleCloseSkuDialog = () => {
    if (skuDialogLoading) {
      return;
    }
    setSkuDialogOpen(false);
    setEditingSku(null);
    setSkuDialogError(null);
    setSkuDialogName('');
    setSkuDialogNumber('');
    setSkuDialogWorkflowId('');
  };

  const handleSaveSku = async () => {
    const trimmedName = skuDialogName.trim();
    const trimmedSkuNumber = skuDialogNumber.trim();
    if (!trimmedName || !trimmedSkuNumber || !selectedPractice?.id || !tokens?.id_token) {
      setSkuDialogError('Name and SKU are required.');
      return;
    }

    setSkuDialogLoading(true);
    setSkuDialogError(null);

    try {
      const payload = {
        organization_id: selectedPractice.id,
        name: trimmedName,
        sku_number: trimmedSkuNumber,
      };
      if (skuDialogWorkflowId) {
        payload.lab_test_workflow_id = skuDialogWorkflowId;
      } else if (editingSku) {
        payload.lab_test_workflow_id = null;
      }

      const isEditing = Boolean(editingSku);
      const response = await fetch(
        isEditing ? `${apiBase}/lab/lab-test-skus/${editingSku.id}` : `${apiBase}/lab/lab-test-skus`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.id_token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let message = isEditing ? 'Failed to update lab test SKU' : 'Failed to create lab test SKU';
        try {
          const data = await response.json();
          if (data?.detail) {
            message = data.detail;
          }
        } catch {
          // Keep fallback.
        }
        throw new Error(message);
      }

      setSkuDialogOpen(false);
      setEditingSku(null);
      setSkuDialogName('');
      setSkuDialogNumber('');
      setSkuDialogWorkflowId('');
      setSkuPage(0);
      setSkuRefreshNonce((value) => value + 1);
    } catch (err) {
      setSkuDialogError(err.message || 'Failed to save lab test SKU');
    } finally {
      setSkuDialogLoading(false);
    }
  };

  const handleArchiveSku = async (sku) => {
    if (!tokens?.id_token) {
      return;
    }
    if (!window.confirm(`Archive SKU ${sku.sku_number}?`)) {
      return;
    }
    try {
      const response = await fetch(`${apiBase}/lab/lab-test-skus/${sku.id}/archive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.id_token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to archive lab test SKU');
      }
      setSkuRefreshNonce((value) => value + 1);
    } catch (err) {
      setSkuError(err.message || 'Failed to archive lab test SKU');
    }
  };

  const filteredWorkflowQuestions = workflowQuestions.filter((question) => {
    if (workflowQuestionsFilter === 'active') {
      return question.is_active;
    }
    if (workflowQuestionsFilter === 'inactive') {
      return !question.is_active;
    }
    return true;
  });

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      {!selectedPractice && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a lab to view workflows.
        </Typography>
      )}

      {!selectedPractice ? (
        <OrganizationEmptyState label="lab" />
      ) : (
        <Card>
          <CardContent>
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Workflows" />
                <Tab label="Lab Test SKUs" />
              </Tabs>
              {tabIndex === 0 ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateWorkflowDialog}
                >
                  New Workflow
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateSkuDialog}
                >
                  Add Lab Test SKU
                </Button>
              )}
            </Box>

            {tabIndex === 0 && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 2,
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Workflow definitions available to the selected lab organization.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'stretch', md: 'center' },
                    }}
                  >
                    <TextField
                      size="small"
                      label="Search Workflows"
                      placeholder="Search by name"
                      value={workflowSearchInput}
                      onChange={(event) => setWorkflowSearchInput(event.target.value)}
                      sx={{ minWidth: 240 }}
                      InputProps={{
                        endAdornment: workflowSearchInput ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setWorkflowSearchInput('')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="workflow-archived-filter-label">Archived</InputLabel>
                      <Select
                        labelId="workflow-archived-filter-label"
                        value={workflowArchivedFilter}
                        label="Archived"
                        onChange={(event) => {
                          setWorkflowArchivedFilter(event.target.value);
                          setWorkflowsPage(0);
                        }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <TableContainer component={Paper}>
                  {workflowsLoading ? (
                    <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Container>
                  ) : workflowsError ? (
                    <Container disableGutters sx={{ py: 3 }}>
                      <Typography color="error">{workflowsError}</Typography>
                    </Container>
                  ) : (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sortDirection={workflowOrderBy === 'name' ? workflowOrderDir : false}>
                            <TableSortLabel
                              active={workflowOrderBy === 'name'}
                              direction={workflowOrderBy === 'name' ? workflowOrderDir : 'asc'}
                              onClick={() => handleWorkflowSort('name')}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={workflowOrderBy === 'question_count' ? workflowOrderDir : false}
                            align="right"
                            sx={{ width: 96, whiteSpace: 'nowrap' }}
                          >
                            <TableSortLabel
                              active={workflowOrderBy === 'question_count'}
                              direction={workflowOrderBy === 'question_count' ? workflowOrderDir : 'asc'}
                              onClick={() => handleWorkflowSort('question_count')}
                            >
                              Questions
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={workflowOrderBy === 'sku_count' ? workflowOrderDir : false}
                            align="right"
                            sx={{ width: 96, whiteSpace: 'nowrap' }}
                          >
                            <TableSortLabel
                              active={workflowOrderBy === 'sku_count'}
                              direction={workflowOrderBy === 'sku_count' ? workflowOrderDir : 'asc'}
                              onClick={() => handleWorkflowSort('sku_count')}
                            >
                              SKUs
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={workflowOrderBy === 'created_at' ? workflowOrderDir : false}
                            align="right"
                            sx={{ width: 224, whiteSpace: 'nowrap' }}
                          >
                            <TableSortLabel
                              active={workflowOrderBy === 'created_at'}
                              direction={workflowOrderBy === 'created_at' ? workflowOrderDir : 'asc'}
                              onClick={() => handleWorkflowSort('created_at')}
                            >
                              Created
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 176, whiteSpace: 'nowrap' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {workflows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary">
                                No workflows found for this lab.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          workflows.map((workflow) => (
                            <TableRow key={workflow.id} hover>
                              <TableCell>{workflow.name}</TableCell>
                              <TableCell align="right">{workflow.question_count || 0}</TableCell>
                              <TableCell align="right">{workflow.sku_count || 0}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {workflow.created_at ? new Date(workflow.created_at).toLocaleString() : 'Unknown'}
                              </TableCell>
                              <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditWorkflowDialog(workflow)}
                                  title="Edit name"
                                  sx={{ mr: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditQuestionsDialog(workflow)}
                                  title="Edit questions"
                                  sx={{ mr: 0.5 }}
                                >
                                  <QuizIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleArchiveWorkflow(workflow)}
                                  title={workflow.archived_at ? 'Archived' : 'Archive workflow'}
                                  disabled={Boolean(workflow.archived_at)}
                                >
                                  <ArchiveIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>

                <TablePagination
                  component="div"
                  count={workflowsTotal}
                  page={workflowsPage}
                  onPageChange={(_, newPage) => setWorkflowsPage(newPage)}
                  rowsPerPage={workflowsRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setWorkflowsRowsPerPage(parseInt(event.target.value, 10));
                    setWorkflowsPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}

            {tabIndex === 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Map known SKUs to the workflow used when that SKU is encountered.
                </Typography>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: { xs: 'stretch', lg: 'center' },
                    gap: 2,
                    flexDirection: { xs: 'column', lg: 'row' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'stretch', md: 'center' },
                    }}
                  >
                    <TextField
                      size="small"
                      label="Search SKUs"
                      placeholder="Search by name"
                      value={skuSearchInput}
                      onChange={(event) => setSkuSearchInput(event.target.value)}
                      sx={{ minWidth: 240 }}
                      InputProps={{
                        endAdornment: skuSearchInput ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSkuSearchInput('')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="lab-test-sku-workflow-filter-label">Workflow</InputLabel>
                      <Select
                        labelId="lab-test-sku-workflow-filter-label"
                        value={skuWorkflowFilter}
                        label="Workflow"
                        onChange={(event) => {
                          setSkuWorkflowFilter(event.target.value);
                          setSkuPage(0);
                        }}
                      >
                        <MenuItem value="__all__">All Workflows</MenuItem>
                        <MenuItem value="__none__">No Workflow</MenuItem>
                        {workflowOptions.map((workflow) => (
                          <MenuItem key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="lab-test-sku-archived-filter-label">Archived</InputLabel>
                      <Select
                        labelId="lab-test-sku-archived-filter-label"
                        value={skuArchivedFilter}
                        label="Archived"
                        onChange={(event) => {
                          setSkuArchivedFilter(event.target.value);
                          setSkuPage(0);
                        }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <TableContainer component={Paper}>
                  {skuLoading ? (
                    <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Container>
                  ) : skuError ? (
                    <Container disableGutters sx={{ py: 3 }}>
                      <Typography color="error">{skuError}</Typography>
                    </Container>
                  ) : (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sortDirection={skuOrderBy === 'name' ? skuOrderDir : false}>
                            <TableSortLabel
                              active={skuOrderBy === 'name'}
                              direction={skuOrderBy === 'name' ? skuOrderDir : 'asc'}
                              onClick={() => handleSkuSort('name')}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={skuOrderBy === 'sku_number' ? skuOrderDir : false}>
                            <TableSortLabel
                              active={skuOrderBy === 'sku_number'}
                              direction={skuOrderBy === 'sku_number' ? skuOrderDir : 'asc'}
                              onClick={() => handleSkuSort('sku_number')}
                            >
                              SKU
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={skuOrderBy === 'workflow_name' ? skuOrderDir : false}>
                            <TableSortLabel
                              active={skuOrderBy === 'workflow_name'}
                              direction={skuOrderBy === 'workflow_name' ? skuOrderDir : 'asc'}
                              onClick={() => handleSkuSort('workflow_name')}
                            >
                              Workflow
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={skuOrderBy === 'created_at' ? skuOrderDir : false}>
                            <TableSortLabel
                              active={skuOrderBy === 'created_at'}
                              direction={skuOrderBy === 'created_at' ? skuOrderDir : 'asc'}
                              onClick={() => handleSkuSort('created_at')}
                            >
                              Created
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skuRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary">
                                No lab test SKUs found for this lab.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          skuRows.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.name}</TableCell>
                              <TableCell>{row.sku_number}</TableCell>
                              <TableCell>{row.workflow_name || 'None'}</TableCell>
                              <TableCell>
                                {row.created_at ? new Date(row.created_at).toLocaleString() : 'Unknown'}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEditSkuDialog(row)}
                                  title="Edit SKU"
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleArchiveSku(row)}
                                  title={row.archived_at ? 'Archived' : 'Archive SKU'}
                                  disabled={Boolean(row.archived_at)}
                                >
                                  <ArchiveIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>

                <TablePagination
                  component="div"
                  count={skuTotal}
                  page={skuPage}
                  onPageChange={(_, newPage) => setSkuPage(newPage)}
                  rowsPerPage={skuRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setSkuRowsPerPage(parseInt(event.target.value, 10));
                    setSkuPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={createWorkflowDialogOpen} onClose={handleCloseCreateWorkflowDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingWorkflow ? 'Edit Workflow Name' : 'New Workflow'}</DialogTitle>
        <DialogContent>
          {createWorkflowError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {createWorkflowError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label="Workflow Name"
            value={newWorkflowName}
            onChange={(event) => setNewWorkflowName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !createWorkflowLoading) {
                event.preventDefault();
                handleSaveWorkflow();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateWorkflowDialog} disabled={createWorkflowLoading}>
            Cancel
          </Button>
          <Button onClick={handleSaveWorkflow} variant="contained" disabled={createWorkflowLoading}>
            {editingWorkflow ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editQuestionsDialogOpen}
        onClose={handleCloseEditQuestionsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Questions{questionsWorkflow?.name ? `: ${questionsWorkflow.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {workflowQuestionsError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {workflowQuestionsError}
            </Alert>
          )}

          <Box
            sx={{
              mt: 1,
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="workflow-question-filter-label">Question Filter</InputLabel>
              <Select
                labelId="workflow-question-filter-label"
                value={workflowQuestionsFilter}
                label="Question Filter"
                onChange={(event) => setWorkflowQuestionsFilter(event.target.value)}
                disabled={workflowQuestionsLoading || workflowQuestionsSaving}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddWorkflowQuestionDraft}
              disabled={workflowQuestionsLoading || workflowQuestionsSaving}
            >
              Add Question
            </Button>
          </Box>

          {workflowQuestionsLoading ? (
            <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Container>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ width: 72 }}>
                      Order
                    </TableCell>
                    <TableCell>Question</TableCell>
                    <TableCell align="center" sx={{ width: 104, whiteSpace: 'nowrap' }}>
                      Type
                    </TableCell>
                    <TableCell align="center" sx={{ width: 112, whiteSpace: 'nowrap' }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ width: 148, whiteSpace: 'nowrap' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkflowQuestions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No questions match the current filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWorkflowQuestions.map((question) => {
                      const actualIndex = workflowQuestions.findIndex(
                        (row) => row.draftId === question.draftId
                      );
                      return (
                        <TableRow key={question.draftId} hover>
                          <TableCell align="right">{actualIndex + 1}</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={question.question_text}
                              onChange={(event) =>
                                handleWorkflowQuestionChange(question.draftId, {
                                  question_text: event.target.value,
                                })
                              }
                              disabled={workflowQuestionsSaving}
                            />
                          </TableCell>
                          <TableCell align="center">{question.answer_type || 'YesNo'}</TableCell>
                          <TableCell align="center">
                            {question.is_active ? 'Active' : 'Inactive'}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveWorkflowQuestionDraft(question.draftId, 'up')}
                              disabled={workflowQuestionsSaving || actualIndex === 0}
                              title="Move up"
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveWorkflowQuestionDraft(question.draftId, 'down')}
                              disabled={workflowQuestionsSaving || actualIndex === workflowQuestions.length - 1}
                              title="Move down"
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <Button
                              size="small"
                              color={question.is_active ? 'warning' : 'inherit'}
                              startIcon={question.is_active ? <ArchiveIcon fontSize="small" /> : <RestoreIcon fontSize="small" />}
                              onClick={() =>
                                handleWorkflowQuestionChange(question.draftId, {
                                  is_active: !question.is_active,
                                })
                              }
                              disabled={workflowQuestionsSaving}
                            >
                              {question.is_active ? 'Remove' : 'Restore'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditQuestionsDialog} disabled={workflowQuestionsSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveWorkflowQuestions}
            variant="contained"
            disabled={workflowQuestionsLoading || workflowQuestionsSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={skuDialogOpen} onClose={handleCloseSkuDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSku ? 'Edit Lab Test SKU' : 'Add Lab Test SKU'}</DialogTitle>
        <DialogContent>
          {skuDialogError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {skuDialogError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label="Name"
            value={skuDialogName}
            onChange={(event) => setSkuDialogName(event.target.value)}
          />
          <TextField
            margin="dense"
            fullWidth
            label="SKU"
            value={skuDialogNumber}
            onChange={(event) => setSkuDialogNumber(event.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="lab-test-sku-workflow-label">Workflow</InputLabel>
            <Select
              labelId="lab-test-sku-workflow-label"
              value={skuDialogWorkflowId}
              label="Workflow"
              onChange={(event) => setSkuDialogWorkflowId(event.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {workflowOptions.map((workflow) => (
                <MenuItem key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSkuDialog} disabled={skuDialogLoading}>
            Cancel
          </Button>
          <Button onClick={handleSaveSku} variant="contained" disabled={skuDialogLoading}>
            {editingSku ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
